import { useState, useEffect } from "react";
import { useAuth } from "@/App";
import GlassCard from "@/components/GlassCard";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { Moon, Heart, Wind, Snowflake, Activity, Play } from "lucide-react";
import { Progress } from "@/components/ui/progress";

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="glass-card !p-3 !rounded-lg text-xs">
      <p className="text-gray-400">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }} className="font-medium">{p.name}: {p.value}</p>
      ))}
    </div>
  );
};

export default function RecoveryPage() {
  const { api } = useAuth();
  const [recoveryData, setRecoveryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [breatheActive, setBreatheActive] = useState(false);

  useEffect(() => {
    api().get("/recovery?days=7").then(r => {
      setRecoveryData(r.data.sort((a, b) => a.date.localeCompare(b.date)));
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [api]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-2 border-[#00f260] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const latest = recoveryData[recoveryData.length - 1] || {};
  const chartData = recoveryData.map(r => ({
    date: r.date?.slice(5) || "",
    Alvás: r.sleep_hours,
    HRV: r.hrv,
    Fáradtság: r.fatigue_level,
    Minőség: r.sleep_quality
  }));

  const fatigueLevel = latest.fatigue_level || 35;
  const fatigueColor = fatigueLevel > 60 ? "#ff5252" : fatigueLevel > 40 ? "#ffc107" : "#00f260";
  const fatigueLabel = fatigueLevel > 60 ? "Fáradt" : fatigueLevel > 40 ? "Közepes" : "Friss";

  const tools = [
    { icon: Wind, name: "Légzőgyakorlat", desc: "4-7-8 légzési technika", color: "#00f260", action: "breathe" },
    { icon: Activity, name: "Nyújtás", desc: "15 perc aktív nyújtás", color: "#ffc107", action: "stretch" },
    { icon: Snowflake, name: "Hideg terápia", desc: "3 perc hideg zuhany", color: "#64b5f6", action: "cold" },
  ];

  return (
    <div className="space-y-6 md:pt-20" data-testid="recovery-page">
      <div className="flex items-center justify-between fade-in-up">
        <h1 className="font-heading text-3xl font-bold text-white tracking-tight">Regeneráció</h1>
        <Heart className="text-[#00f260]" size={24} />
      </div>

      {/* Sleep Chart */}
      <GlassCard testId="sleep-chart-card" className="fade-in-up">
        <div className="flex items-center gap-2 mb-4">
          <Moon size={16} className="text-[#00f260]" />
          <p className="text-xs text-gray-400 uppercase tracking-wider">Alvás idő (7 nap)</p>
        </div>
        <div className="h-44">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData}>
              <XAxis dataKey="date" tick={{ fill: "#6b7280", fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis hide domain={[0, 10]} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="Alvás" fill="#00f260" radius={[6, 6, 0, 0]} opacity={0.7} name="Alvás (óra)" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </GlassCard>

      {/* HRV Trend */}
      <GlassCard testId="hrv-recovery-chart" className="fade-in-up fade-in-up-1">
        <div className="flex items-center gap-2 mb-4">
          <Heart size={16} className="text-[#00f260]" />
          <p className="text-xs text-gray-400 uppercase tracking-wider">HRV trend</p>
        </div>
        <div className="h-40">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              <XAxis dataKey="date" tick={{ fill: "#6b7280", fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis hide />
              <Tooltip content={<CustomTooltip />} />
              <Line type="monotone" dataKey="HRV" stroke="#00f260" strokeWidth={2} dot={{ fill: "#00f260", r: 3 }} name="HRV (ms)" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </GlassCard>

      {/* Fatigue Meter */}
      <GlassCard testId="fatigue-meter-card" className="fade-in-up fade-in-up-2">
        <p className="text-xs text-gray-400 uppercase tracking-wider mb-4">Fáradtság mérő</p>
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <div className="flex justify-between mb-2">
              <span className="text-sm text-white font-medium">{fatigueLabel}</span>
              <span className="text-sm font-medium" style={{ color: fatigueColor }}>{fatigueLevel}%</span>
            </div>
            <div className="h-3 bg-white/5 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-700"
                style={{ width: `${fatigueLevel}%`, backgroundColor: fatigueColor }}
              />
            </div>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3 mt-4">
          <div className="text-center p-2 bg-white/5 rounded-xl">
            <p className="text-[10px] text-gray-400">Alvás</p>
            <p className="text-sm text-white font-medium">{latest.sleep_hours || 7.5}h</p>
          </div>
          <div className="text-center p-2 bg-white/5 rounded-xl">
            <p className="text-[10px] text-gray-400">Minőség</p>
            <p className="text-sm text-white font-medium">{latest.sleep_quality || 85}%</p>
          </div>
          <div className="text-center p-2 bg-white/5 rounded-xl">
            <p className="text-[10px] text-gray-400">Mobility</p>
            <p className="text-sm text-white font-medium">{latest.mobility_score || 75}%</p>
          </div>
        </div>
      </GlassCard>

      {/* Mobility Recommendation */}
      <GlassCard testId="mobility-recommendation-card" className="fade-in-up fade-in-up-3">
        <p className="text-xs text-gray-400 uppercase tracking-wider mb-3">Mobility ajánlás</p>
        <p className="text-sm text-white/80 leading-relaxed">
          A mai regenerációs adatok alapján ajánlott egy könnyű mobility edzés. Fókuszálj a csípő és gerinc
          mobilizálására, valamint a hátsó lánc nyújtására.
        </p>
      </GlassCard>

      {/* Recovery Tools */}
      <div className="space-y-3">
        <p className="text-xs text-gray-400 uppercase tracking-wider fade-in-up fade-in-up-3">Eszközök</p>
        {tools.map((tool, i) => {
          const Icon = tool.icon;
          return (
            <GlassCard key={tool.name} className={`fade-in-up fade-in-up-${i + 4}`} testId={`tool-${tool.action}`}>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${tool.color}15` }}>
                  <Icon size={22} style={{ color: tool.color }} />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-white font-medium">{tool.name}</p>
                  <p className="text-xs text-gray-400">{tool.desc}</p>
                </div>
                <button
                  data-testid={`start-${tool.action}-btn`}
                  className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center hover:bg-white/10 transition-colors"
                  onClick={() => {
                    if (tool.action === "breathe") setBreatheActive(!breatheActive);
                  }}
                >
                  <Play size={14} className="text-white ml-0.5" />
                </button>
              </div>
            </GlassCard>
          );
        })}
      </div>

      {/* Breathing Exercise Active */}
      {breatheActive && (
        <GlassCard glow testId="breathing-exercise-active" className="text-center py-8 fade-in-up">
          <p className="text-xs text-gray-400 uppercase tracking-widest mb-4">Légzőgyakorlat</p>
          <div className="w-24 h-24 mx-auto rounded-full border-2 border-[#00f260] breathe-pulse flex items-center justify-center mb-4">
            <Wind size={32} className="text-[#00f260]" />
          </div>
          <p className="text-sm text-white mb-2">Lélegezz be 4 másodpercig... tartsd 7-ig... engedd ki 8-ig</p>
          <button onClick={() => setBreatheActive(false)} className="text-xs text-gray-400 hover:text-white transition-colors mt-2">
            Bezárás
          </button>
        </GlassCard>
      )}

      {/* Start Recovery */}
      <button
        data-testid="start-recovery-btn"
        className="neon-btn w-full text-sm"
        onClick={() => setBreatheActive(true)}
      >
        Regeneráció indítása
      </button>
    </div>
  );
}
